import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { CalendlyButton } from '../components/CalendlyButton';
import { blogArticles } from './Blog';
import { 
  ClipboardCheck, 
  GraduationCap, 
  Building2, 
  Users, 
  MapPin, 
  ArrowRight,
  CheckCircle2,
  Briefcase,
  Calendar,
  Clock
} from 'lucide-react';

const Home = () => {
  const formatDate = (dateString) => {
    const options = { year: 'numeric', month: 'long', day: 'numeric' };
    return new Date(dateString).toLocaleDateString('fr-FR', options);
  };

  const latestArticles = [...blogArticles]
    .sort((a, b) => new Date(b.date) - new Date(a.date))
    .slice(0, 3);

  return (
    <>
      <Helmet>
        <title>Formateur & Auditeur Qualité dans le Vaucluse | Mehdi Richa</title>
        <meta 
          name="description" 
          content="Mehdi Richa, formateur polyvalent et auditeur qualité dans le Vaucluse. Formation logistique, qualité, management. Audits internes et préparation certifications. Avignon, Carpentras, Orange." 
        />
        <meta property="og:title" content="Mehdi Richa - Formateur & Auditeur Qualité Vaucluse" />
        <meta property="og:description" content="Expert en formation et audit qualité dans le Vaucluse. Accompagnement personnalisé pour PME, organismes de formation et collectivités." />
        <meta property="og:type" content="website" />
        <link rel="canonical" href={`${window.location.origin}/`} />
      </Helmet>

      {/* Hero Section */}
      <section className="relative bg-slate-50 overflow-hidden" data-testid="hero-section">
        <div className="absolute inset-0 bg-gradient-to-br from-blue-50 to-slate-50" />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 md:py-24 relative">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="animate-fade-in">
              <span className="inline-block px-4 py-2 bg-blue-100 text-blue-700 rounded-full text-sm font-medium mb-6">
                Basé dans le Vaucluse
              </span>
              <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl font-bold text-slate-900 tracking-tight leading-tight mb-6">
                Formateur & auditeur qualité dans le Vaucluse
              </h1>
              <p className="text-lg text-slate-600 leading-relaxed mb-8 max-w-xl">
                Je suis <strong>Mehdi Richa</strong>, formateur polyvalent et consultant qualité basé à Avignon. 
                J'accompagne les entreprises, organismes de formation et collectivités dans leurs 
                démarches de formation et d'amélioration continue.
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <CalendlyButton 
                  size="lg"
                  className="rounded-full bg-blue-600 hover:bg-blue-700 px-8 py-6 text-base"
                >
                  Prendre rendez-vous
                </CalendlyButton>
                <Button 
                  asChild 
                  variant="outline" 
                  size="lg"
                  className="rounded-full px-8 py-6 text-base border-slate-300 hover:bg-slate-100"
                  data-testid="hero-cta-services"
                >
                  <Link to="/formateur-polyvalent-vaucluse">
                    Découvrir mes services
                  </Link>
                </Button>
              </div>
            </div>
            <div className="animate-fade-in animation-delay-200 hidden lg:block">
              <div className="relative">
                <div className="absolute -inset-4 bg-blue-600/10 rounded-3xl transform rotate-3" />
                <img
                  src="https://images.unsplash.com/photo-1768796370577-c6e8b708b980?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzF8MHwxfHNlYXJjaHwxfHxwcm9mZXNzaW9uYWwlMjBjb3Jwb3JhdGUlMjB0cmFpbmluZyUyMHdvcmtzaG9wfGVufDB8fHx8MTc2OTk0NzcxMHww&ixlib=rb-4.1.0&q=85"
                  alt="Formation professionnelle en entreprise"
                  className="relative rounded-2xl shadow-2xl w-full h-auto object-cover"
                  loading="eager"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Services Grid - Bento Style */}
      <section className="py-20 md:py-28 bg-white" data-testid="services-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16 animate-fade-in">
            <h2 className="font-serif text-3xl sm:text-4xl font-bold text-slate-900 mb-4">
              Mes domaines d'expertise
            </h2>
            <p className="text-slate-600 max-w-2xl mx-auto">
              Une double compétence au service de votre organisation : formation sur mesure et audit qualité
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-8">
            {/* Formation Card */}
            <Card className="group bg-white border border-slate-100 shadow-sm hover:shadow-xl transition-shadow duration-300 overflow-hidden animate-fade-in animation-delay-100" data-testid="service-card-formation">
              <div className="relative h-56 overflow-hidden">
                <img
                  src="https://images.unsplash.com/photo-1758691736067-b309ee3ef7b9?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzF8MHwxfHNlYXJjaHwyfHxwcm9mZXNzaW9uYWwlMjBjb3Jwb3JhdGUlMjB0cmFpbmluZyUyMHdvcmtzaG9wfGVufDB8fHx8MTc2OTk0NzcxMHww&ixlib=rb-4.1.0&q=85"
                  alt="Session de formation"
                  className="w-full h-full object-cover transform group-hover:scale-105 transition-transform duration-500"
                  loading="lazy"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-slate-900/60 to-transparent" />
                <div className="absolute bottom-4 left-4">
                  <span className="inline-flex items-center gap-2 px-3 py-1 bg-white/90 rounded-full text-sm font-medium text-slate-900">
                    <GraduationCap className="w-4 h-4 text-blue-600" />
                    Formation
                  </span>
                </div>
              </div>
              <CardContent className="p-6">
                <h3 className="font-serif text-2xl font-bold text-slate-900 mb-3">
                  Formateur polyvalent dans le Vaucluse
                </h3>
                <p className="text-slate-600 mb-4 leading-relaxed">
                  Formateur logistique Vaucluse, j'interviens également en qualité, management et économie-gestion. 
                  De la salle de classe aux équipes professionnelles, j'adapte ma pédagogie à chaque public.
                </p>
                <ul className="space-y-2 mb-6">
                  <li className="flex items-center gap-2 text-slate-700">
                    <CheckCircle2 className="w-5 h-5 text-blue-600 flex-shrink-0" />
                    Logistique, transport, supply chain
                  </li>
                  <li className="flex items-center gap-2 text-slate-700">
                    <CheckCircle2 className="w-5 h-5 text-blue-600 flex-shrink-0" />
                    Qualité & amélioration continue
                  </li>
                  <li className="flex items-center gap-2 text-slate-700">
                    <CheckCircle2 className="w-5 h-5 text-blue-600 flex-shrink-0" />
                    Management & CEJM
                  </li>
                </ul>
                <Button 
                  asChild 
                  className="rounded-full bg-blue-600 hover:bg-blue-700 w-full"
                  data-testid="cta-formation"
                >
                  <Link to="/formateur-polyvalent-vaucluse">
                    En savoir plus
                    <ArrowRight className="ml-2 h-4 w-4" />
                  </Link>
                </Button>
              </CardContent>
            </Card>

            {/* Audit Card */}
            <Card className="group bg-white border border-slate-100 shadow-sm hover:shadow-xl transition-shadow duration-300 overflow-hidden animate-fade-in animation-delay-200" data-testid="service-card-audit">
              <div className="relative h-56 overflow-hidden">
                <img
                  src="https://images.unsplash.com/photo-1564846824194-346b7871b855?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NzB8MHwxfHNlYXJjaHwxfHxidXNpbmVzcyUyMGhhbmRzJTIwd3JpdGluZyUyMG9uJTIwcGFwZXIlMjBjbG9zZSUyMHVwJTIwYXVkaXQlMjBjaGVja2xpc3R8ZW58MHx8fHwxNzY5OTQ3NzMzfDA&ixlib=rb-4.1.0&q=85"
                  alt="Audit qualité"
                  className="w-full h-full object-cover transform group-hover:scale-105 transition-transform duration-500"
                  loading="lazy"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-slate-900/60 to-transparent" />
                <div className="absolute bottom-4 left-4">
                  <span className="inline-flex items-center gap-2 px-3 py-1 bg-white/90 rounded-full text-sm font-medium text-slate-900">
                    <ClipboardCheck className="w-4 h-4 text-blue-600" />
                    Audit
                  </span>
                </div>
              </div>
              <CardContent className="p-6">
                <h3 className="font-serif text-2xl font-bold text-slate-900 mb-3">
                  Auditeur qualité au service de vos projets
                </h3>
                <p className="text-slate-600 mb-4 leading-relaxed">
                  Consultant qualité Vaucluse, je vous apporte un regard externe, neutre et constructif 
                  sur vos pratiques. Préparation aux certifications, audits internes, diagnostics.
                </p>
                <ul className="space-y-2 mb-6">
                  <li className="flex items-center gap-2 text-slate-700">
                    <CheckCircle2 className="w-5 h-5 text-blue-600 flex-shrink-0" />
                    Audits internes & diagnostics
                  </li>
                  <li className="flex items-center gap-2 text-slate-700">
                    <CheckCircle2 className="w-5 h-5 text-blue-600 flex-shrink-0" />
                    Préparation certifications
                  </li>
                  <li className="flex items-center gap-2 text-slate-700">
                    <CheckCircle2 className="w-5 h-5 text-blue-600 flex-shrink-0" />
                    Revue documentaire
                  </li>
                </ul>
                <Button 
                  asChild 
                  className="rounded-full bg-blue-600 hover:bg-blue-700 w-full"
                  data-testid="cta-audit"
                >
                  <Link to="/audit-qualite-vaucluse">
                    En savoir plus
                    <ArrowRight className="ml-2 h-4 w-4" />
                  </Link>
                </Button>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* For Who Section */}
      <section className="py-20 md:py-28 bg-slate-50" data-testid="clients-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16 animate-fade-in">
            <h2 className="font-serif text-3xl sm:text-4xl font-bold text-slate-900 mb-4">
              Pour qui j'interviens ?
            </h2>
            <p className="text-slate-600 max-w-2xl mx-auto">
              Des structures variées qui partagent un même besoin : monter en compétences et améliorer leurs pratiques
            </p>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            <Card className="bg-white border-0 shadow-sm hover:shadow-md transition-shadow duration-300 text-center p-8 animate-fade-in animation-delay-100" data-testid="client-pme">
              <div className="w-16 h-16 mx-auto mb-4 bg-blue-100 rounded-2xl flex items-center justify-center">
                <Building2 className="w-8 h-8 text-blue-600" />
              </div>
              <h3 className="font-semibold text-lg text-slate-900 mb-2">PME / TPE</h3>
              <p className="text-slate-600 text-sm">
                Entreprises souhaitant structurer leurs processus et former leurs équipes
              </p>
            </Card>

            <Card className="bg-white border-0 shadow-sm hover:shadow-md transition-shadow duration-300 text-center p-8 animate-fade-in animation-delay-200" data-testid="client-of">
              <div className="w-16 h-16 mx-auto mb-4 bg-blue-100 rounded-2xl flex items-center justify-center">
                <GraduationCap className="w-8 h-8 text-blue-600" />
              </div>
              <h3 className="font-semibold text-lg text-slate-900 mb-2">Organismes de formation</h3>
              <p className="text-slate-600 text-sm">
                Centres de formation cherchant des formateurs experts dans leurs domaines
              </p>
            </Card>

            <Card className="bg-white border-0 shadow-sm hover:shadow-md transition-shadow duration-300 text-center p-8 animate-fade-in animation-delay-300" data-testid="client-collectivites">
              <div className="w-16 h-16 mx-auto mb-4 bg-blue-100 rounded-2xl flex items-center justify-center">
                <Briefcase className="w-8 h-8 text-blue-600" />
              </div>
              <h3 className="font-semibold text-lg text-slate-900 mb-2">Collectivités</h3>
              <p className="text-slate-600 text-sm">
                Structures publiques engagées dans des démarches qualité ou de formation
              </p>
            </Card>

            <Card className="bg-white border-0 shadow-sm hover:shadow-md transition-shadow duration-300 text-center p-8 animate-fade-in animation-delay-400" data-testid="client-ecoles">
              <div className="w-16 h-16 mx-auto mb-4 bg-blue-100 rounded-2xl flex items-center justify-center">
                <Users className="w-8 h-8 text-blue-600" />
              </div>
              <h3 className="font-semibold text-lg text-slate-900 mb-2">Établissements scolaires</h3>
              <p className="text-slate-600 text-sm">
                Lycées, BTS, CFA, écoles supérieures pour des interventions pédagogiques
              </p>
            </Card>
          </div>
        </div>
      </section>

      {/* Zone d'intervention */}
      <section className="py-20 md:py-28 bg-white" data-testid="zone-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="animate-fade-in">
              <h2 className="font-serif text-3xl sm:text-4xl font-bold text-slate-900 mb-6">
                Zone d'intervention dans le Vaucluse
              </h2>
              <p className="text-slate-600 leading-relaxed mb-6">
                Basé à <strong>Avignon</strong>, j'interviens principalement dans tout le <strong>Vaucluse</strong> : 
                Carpentras, Orange, Cavaillon, L'Isle-sur-la-Sorgue, Apt, et leurs environs.
              </p>
              <p className="text-slate-600 leading-relaxed mb-8">
                Pour les missions ne nécessitant pas de présence sur site, j'interviens également 
                <strong> à distance</strong> via visioconférence, quel que soit votre lieu d'implantation.
              </p>
              <div className="flex flex-wrap gap-3 mb-8">
                {['Avignon', 'Carpentras', 'Orange', 'Cavaillon', 'Apt', 'L\'Isle-sur-la-Sorgue'].map((city) => (
                  <span 
                    key={city}
                    className="inline-flex items-center gap-1 px-4 py-2 bg-slate-100 rounded-full text-sm font-medium text-slate-700"
                  >
                    <MapPin className="w-4 h-4 text-blue-600" />
                    {city}
                  </span>
                ))}
              </div>
              <Button 
                asChild 
                size="lg"
                className="rounded-full bg-blue-600 hover:bg-blue-700"
                data-testid="cta-contact-zone"
              >
                <a href="mailto:mehdi.richa.formation@gmail.com">
                  Parlons de votre projet
                  <ArrowRight className="ml-2 h-5 w-5" />
                </a>
              </Button>
            </div>
            <div className="animate-fade-in animation-delay-200">
              <div className="relative">
                <div className="absolute -inset-4 bg-blue-600/10 rounded-3xl transform -rotate-3" />
                <img
                  src="https://images.unsplash.com/photo-1758814841560-4b2966459567?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzNDR8MHwxfHNlYXJjaHwxfHxhdmlnbm9uJTIwYnJpZGdlJTIwcGFsYWlzJTIwZGVzJTIwcGFwZXMlMjBsYW5kbWFya3xlbnwwfHx8fDE3Njk5NDc3MjB8MA&ixlib=rb-4.1.0&q=85"
                  alt="Pont d'Avignon, Vaucluse"
                  className="relative rounded-2xl shadow-xl w-full h-auto object-cover"
                  loading="lazy"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Latest Blog Articles */}
      <section className="py-20 md:py-28 bg-slate-50" data-testid="home-blog-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between mb-12 gap-6 animate-fade-in">
            <div>
              <span className="inline-block px-4 py-2 bg-blue-100 text-blue-700 rounded-full text-sm font-medium mb-4">
                Blog
              </span>
              <h2 className="font-serif text-3xl sm:text-4xl font-bold text-slate-900 mb-3">
                Derniers articles & conseils
              </h2>
              <p className="text-slate-600 max-w-xl">
                Mes réflexions sur l'audit qualité, la formation et l'amélioration continue.
              </p>
            </div>
            <Button 
              asChild 
              variant="outline"
              className="rounded-full border-slate-300 hover:bg-white self-start sm:self-end"
              data-testid="home-blog-see-all"
            >
              <Link to="/blog">
                Voir tous les articles
                <ArrowRight className="ml-2 h-4 w-4" />
              </Link>
            </Button>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {latestArticles.map((article, index) => (
              <Card 
                key={article.id}
                className="group overflow-hidden border-0 shadow-sm hover:shadow-xl transition-all duration-300 animate-fade-in bg-white"
                style={{ animationDelay: `${(index + 1) * 100}ms` }}
                data-testid={`home-article-${article.id}`}
              >
                <Link to={`/blog/${article.slug}`} className="block">
                  <div className="relative h-48 overflow-hidden">
                    <img
                      src={article.image}
                      alt={article.title}
                      className="absolute inset-0 w-full h-full object-cover transform group-hover:scale-105 transition-transform duration-500"
                      loading="lazy"
                    />
                    <div className="absolute top-4 left-4">
                      <span className="px-3 py-1 bg-white/90 backdrop-blur-sm rounded-full text-xs font-medium text-slate-900">
                        {article.category}
                      </span>
                    </div>
                  </div>
                  <CardContent className="p-6">
                    <div className="flex items-center gap-4 mb-3 text-xs text-slate-500">
                      <span className="flex items-center gap-1">
                        <Calendar className="w-3.5 h-3.5" />
                        {formatDate(article.date)}
                      </span>
                      <span className="flex items-center gap-1">
                        <Clock className="w-3.5 h-3.5" />
                        {article.readTime}
                      </span>
                    </div>
                    <h3 className="font-serif text-xl font-bold text-slate-900 mb-3 line-clamp-2 group-hover:text-blue-600 transition-colors">
                      {article.title}
                    </h3>
                    <p className="text-slate-600 text-sm leading-relaxed line-clamp-3 mb-4">
                      {article.excerpt}
                    </p>
                    <span className="inline-flex items-center text-blue-600 font-medium text-sm group-hover:gap-2 transition-all">
                      Lire l'article
                      <ArrowRight className="ml-1 h-4 w-4" />
                    </span>
                  </CardContent>
                </Link>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Final */}
      <section className="py-20 md:py-28 bg-slate-900" data-testid="cta-section">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="font-serif text-3xl sm:text-4xl font-bold text-white mb-6 animate-fade-in">
            Un projet de formation ou d'audit qualité ?
          </h2>
          <p className="text-slate-300 text-lg mb-10 animate-fade-in animation-delay-100">
            Discutons ensemble de vos besoins. Je vous propose un premier échange gratuit 
            pour comprendre votre contexte et vous proposer une solution adaptée.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center animate-fade-in animation-delay-200">
            <CalendlyButton 
              size="lg"
              className="rounded-full bg-white text-slate-900 hover:bg-slate-100 px-10 py-6 text-lg"
            >
              Prendre rendez-vous
            </CalendlyButton>
            <Button 
              asChild 
              variant="outline"
              size="lg"
              className="rounded-full border-white text-white hover:bg-white/10 px-10 py-6 text-lg"
              data-testid="cta-final-email"
            >
              <a href="mailto:mehdi.richa.formation@gmail.com">
                M'envoyer un email
                <ArrowRight className="ml-2 h-5 w-5" />
              </a>
            </Button>
          </div>
        </div>
      </section>
    </>
  );
};

export default Home;
